import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import React, { Component } from "react";
import PageContent from "@my-dld/components/PageContent";
import Strings, { LVStrings } from "@my-dld/assets/strings";
import { AssetCard } from "@10x/dubai-rest-api/dist/property_registry/entity";
import { DashboardBCItem } from "@my-dld/contexts/PageContext";
import PageTitle from "@my-dld/components/PageTitle";
import {
	MSChargeDetails,
	MSChargeDocuments,
	MSChargeInvoiceSummary,
	MSChargePayment,
	MSChargeType,
	PaymentType
} from "@eres/mollakservicechargeapi/entities";
import { loadMSChargeDetails, loadMSChargeStatement } from "@eres/mollakservicechargeapi/api";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { getLocalValue } from "@eres/core/dist/LocaleValue";
import { toFormattedCurrency } from "@eres/core/dist/Currency";
import BreakdownPopup from "@my-dld/components/MollakServiceCharge/BreakdownPopup";
import PaymentFrame from "../TabuRequest/PaymentFrame";
import { LocaleUtil, StringUtil } from "@eres/core";
import { openDocument, openPayment } from "@my-dld/utils/Utils";
import { isEmptyStringOrNull } from "@eres/core/dist/StringUtils";
import { Redirect } from "react-router";
import { replaceNavigate } from "@my-dld/utils/StoreUtils";
import { getUnitNumber, getVillaName, TabuPropertyType } from "@my-dld/utils/PropertyUtils";
import MollakPayment from "../../components/MollakServiceCharge/MollakPayment";
import { trackEventWithData } from "@my-dld/utils/Analytics";
import { MSCContext } from "@my-dld/contexts/MSCContext";
import { getAppServices } from "@10x/coreapp";
import { extractPropInfo } from "../MyDLD/PropertyDetails";

interface Props extends RoutedViewProps<{ propertyTypeId: string; propertyId: string }> {

}

interface State {
	property: AssetCard;
	details?: MSChargeDetails;
	showBreakdown?: boolean;
	paymentTypes: PaymentType[];
	paymentUrl?: string;
}

class MollakServiceChargeDetails extends Component<Props, State> {
	static contextType = MSCContext;
	context!: React.ContextType<typeof MSCContext>;
	payment = React.createRef<PaymentFrame>();

	constructor( props: Props ) {
		super( props );
		let property = props.location.state ? props.location.state.property : undefined;
		const { propertyTypeId, propertyId } = props.match.params;
		if ( !property ) {
			let type = Number( propertyTypeId );
			let id = Number( propertyId );
			let assetCards = getAppServices().storeService.getStore().getState().UserData.properties;
			if ( assetCards ) {
				property = assetCards.find( ( p: AssetCard ) => {
					return p.propertyId === id && p.propertyTypeId === type;
				} );
			}
		}
		this.state = {
			property: property,
			paymentTypes: [],
		};
	}

	private static getPaymentTypes( payment?: MSChargePayment ) {
		const paymentTypes: PaymentType[] = [];
		if ( payment ) {
			if ( !isEmptyStringOrNull( payment.paymentLinkInvoice ) ) {
				const isExternal = payment.isExternalLink;
				paymentTypes.push( {
					id: 1,
					title: isExternal ? LVStrings.management_company_text : LVStrings.noqodi,
					link: payment.paymentLinkInvoice,
					isExternal: isExternal,
				} );
			}
			if ( !isEmptyStringOrNull( payment.noqodiPaymentLink ) ) {
				paymentTypes.push( {
					id: 2,
					title: LVStrings.noqodi,
					link: payment.noqodiPaymentLink,
					isExternal: false
				} );
			}
			if ( !isEmptyStringOrNull( payment.companyPaymentLink ) ) {
				paymentTypes.push( {
					id: 3,
					title: LVStrings.management_company_text,
					link: payment.companyPaymentLink,
					isExternal: true
				} );
			}
			if ( !isEmptyStringOrNull( payment.pgPaymentLink ) ) {
				paymentTypes.push( {
					id: 4,
					title: LVStrings.management_company_text,
					link: payment.pgPaymentLink,
					isExternal: true
				} );
			}
		}
		return paymentTypes;
	}

	componentDidMount() {
		this.loadServiceChargeDetails();
	}

	render() {
		const { property, details, showBreakdown, paymentTypes, paymentUrl } = this.state;
		if ( property == null )
			return <Redirect to={ "/mydld" }/>;
		const { val, lbl } = extractPropInfo( property );
		return <PageContent
			title={ `${ Strings.last_service_charge_invoice } - ${ lbl } : ${ val }` }
			hideQLinks={ true }>
			<PageTitle title={ Strings.mollak_service_charges } paths={ [ DashboardBCItem ] }/>
			{ details && <>
				<div className={ "row" }>
					<RenderServiceChargeInfo
						property={ property }
						summary={ details.InvoiceSummary }
						onMoreInfo={ this.onShowBreakdown }/>
					{ paymentTypes.length > 0 && <MollakPayment
						details={ details }
						paymentTypes={ paymentTypes }
						onPay={ this.onPay }/> }
				</div>
				{ showBreakdown && <BreakdownPopup
					chargesBreakDown={ details.chargesBreakdown }
					onDismiss={ this.onDismissBreakdown }/> }
				<RenderDocuments
					docs={ details.documents } onOpenDoc={ this.onOpenDocument }
					onStatement={ this.getStatementDocUrl }/>
			</> }
			{ paymentUrl && <PaymentFrame
				ref={ this.payment }
				url={ paymentUrl }
				onDismiss={ this.onPaymentDismiss }/> }
		</PageContent>;
	}

	private loadServiceChargeDetails() {
		const { property } = this.state;
		if ( property ) {
			showLoading();
			loadMSChargeDetails( property.tabuPropertyId ).then( ( resp: MSChargeDetails ) => {
				hideLoading();
				this.setState( {
					details: resp,
					paymentTypes: MollakServiceChargeDetails.getPaymentTypes( resp.payment ),
				} );
			} ).catch( ( reason ) => {
				hideLoading();
				showErrorMessage( reason );
			} );
		}
	}

	private onShowBreakdown = () => {
		this.setState( {
			showBreakdown: true,
		} );
	};

	private onDismissBreakdown = () => {
		this.setState( {
			showBreakdown: false,
		} );
	};

	private onPay = ( type: PaymentType ) => {
		if ( type.isExternal ) {
			this.trackMollakEvent( "ExternalPayment" );
			openPayment( type.link );
		} else {
			this.trackMollakEvent( "PaymentInitiated" );
			this.openPaymentUrl( type.link );
		}
	};

	private onPaymentDismiss = () => {
		this.setState( {
			paymentUrl: undefined,
		} );
	};

	private getStatementDocUrl = () => {
		showLoading();
		const { property } = this.state;
		loadMSChargeStatement( property.tabuPropertyId ).then( ( resp: { fileURL: string } ) => {
			hideLoading();
			const statementUrl = resp.fileURL.replace( /\\/g, "/" );
			openDocument( statementUrl, Strings.statement_of_account );
		} ).catch( ( reason ) => {
			showErrorMessage( reason );
			hideLoading();
		} );
	};

	private onOpenDocument = ( title: string, docUrl: string ) => {
		docUrl = docUrl.replace( /\\/g, "/" );
		openDocument( docUrl, title );
	};

	private openPaymentUrl( paymentUrl: string ) {
		let win: Window | null = null;
		let handled = false;
		window.name = "MyDLD";
		const listener = ( event: any ) => {
			const data: any = event.data;
			if ( data ) {
				const type = data.type;
				if ( type === "eres_pay" ) {
					if ( this.payment.current )
						this.payment.current.dismiss();
					this.setState( { paymentUrl: undefined } );
					window.removeEventListener( "message", listener );
					window.open( "", "MyDLD" )?.focus();
					if ( win ) {
						handled = true;
						if ( win.close )
							win.close();
						window.focus();
					}
					setTimeout( () => {
						hideLoading();
						if ( data.data ) {
							if ( !StringUtil.isEmptyStringOrNull( data.data.error ) ) {
								this.trackMollakEvent( "PaymentFailed" );
								showErrorMessage( data.data.error );
							} else if ( !StringUtil.isEmptyStringOrNull( data.data.refNumber ) ) {
								this.onPaymentSuccess( data.data.refNumber );
							}
						}
					}, 1 );
				}
			}
		};
		window.addEventListener( "message", listener, false );
		if ( window.AppInfo.usePaymentFrame ) {
			this.setState( {
				paymentUrl: paymentUrl
			} );
		} else {
			win = openPayment( paymentUrl );
			if ( win ) {
				showLoading();
				const pollTimer = window.setInterval( function () {
					if ( !handled && win!.closed ) {
						window.clearInterval( pollTimer );
						window.removeEventListener( "message", listener );
						hideLoading();
					}
				}, 200 );
			} else {
				console.log( "no window" );
				showErrorMessage( "Enable Popup window to continue" );
			}
		}
	}

	private onPaymentSuccess( ref: string ) {
		const { property } = this.state;
		this.trackMollakEvent( "PaymentSuccess", ref );
		this.context.setData( "MSCharges", undefined );
		replaceNavigate( `/mydld/mollak_service_charges/success/${ property.propertyTypeId }/${ property.propertyId }/${ ref }`,
			undefined, { property: property } );
	}

	private trackMollakEvent( action: string, ref?: string ) {
		const { property, details } = this.state;
		const data: any = {
			PropertyId: property.tabuPropertyId + "",
			InvoiceNumber: details!.InvoiceSummary.invoiceNumber,
		};
		if ( ref ) {
			data[ "NoqoodiRef" ] = ref;
		}
		trackEventWithData( "Mollak", action, data );
	}
}

function RenderServiceChargeInfo( props: { property: AssetCard, summary: MSChargeInvoiceSummary, onMoreInfo: () => void } ) {
	const { property, summary, onMoreInfo } = props;
	const { unitNo, area, buildingName } = getPropertyInfo( property );
	const chargeTypes = ( summary && summary.chargeTypes ) ? summary.chargeTypes : undefined;
	return <>
		<div className="col-md-7">
			<div className="payment-title">
				<span className="font-weight-bold">{ Strings.property_details }</span>
			</div>
			<div className="flex-grid mb-3">
				{ !isEmptyStringOrNull( area ) && <div>
					<span>{ Strings.area }</span>
					<span>{ area }</span>
				</div> }
				{ !isEmptyStringOrNull( buildingName ) && <div>
					<span>{ Strings.building_name }</span>
					<span>{ buildingName }</span>
				</div> }
				{ !isEmptyStringOrNull( unitNo ) && <div>
					<span>{ Strings.Unit_Number_Label }</span>
					<span>{ unitNo }</span>
				</div> }
			</div>
			<div className="payment-title">
				<span className="font-weight-bold">{ Strings.invoice_details }</span>
				<span>
                    <a
	                    className="btn-link font-size14 font-weight-normal"
	                    onClick={ onMoreInfo }>{ Strings.more_info }</a>
                </span>
			</div>
			<div className="flex-grid mb-0">
				{ chargeTypes && chargeTypes.map( ( chargeType: MSChargeType ) => {
					const clsName = chargeType.id === -1 ? "font-weight-bold" : "";
					return <div key={ "charge_type_" + chargeType.nameEn }>
						<span className={ clsName }>{ getLocalValue( chargeType ) }</span>
						<span
							className={ clsName }>{ toFormattedCurrency( Number( chargeType.amount ) ) + " " + Strings.aed }</span>
					</div>;
				} ) }
				<div>
					<span>{ Strings.total_vat }</span>
					<span>{ toFormattedCurrency( Number( summary.vatAmount ) ) + " " + Strings.aed }</span>
				</div>
			</div>
		</div>
	</>;
}

function getPropertyInfo( property: AssetCard ) {
	let unitNo, area, buildingName;
	switch ( property.propertyTypeId ) {
		case TabuPropertyType.VILLA:
		case TabuPropertyType.BUILDING:
			buildingName = getVillaName( property );
			area = LocaleUtil.getLocalValue( property.area );
			break;

		case TabuPropertyType.UNIT:
			unitNo = getUnitNumber( property );
			buildingName = LocaleUtil.getLocalValue( ( property as any ).buildingName );
			area = LocaleUtil.getLocalValue( property.area );
			break;
	}
	return { unitNo, area, buildingName };
}

function RenderDocuments( props: {
	docs: MSChargeDocuments, onOpenDoc: ( title: string, docUrl: string ) => void,
	onStatement: () => void,
} ) {
	const { docs, onOpenDoc, onStatement } = props;
	return <div className="row mt-4">
		<div className="col-md-12 content-section">
			<div className="content-section-title">
				<span className="section-title-span">{ Strings.download }</span>
			</div>
		</div>
		<div className="col-md-12 pdf-tempalte-wrapper">
			{ !isEmptyStringOrNull( docs.noticeUrl ) && <div className="pdf-tempalte">
				<div>
                    <span className="pdf-icon">
                        <i className="icon-pdf-o file-formate-icon"/>
                    </span>
					<span className="pdf-title">{ Strings.approval_notice }</span>
				</div>
				<a
					onClick={ () => onOpenDoc( Strings.approval_notice, docs.noticeUrl ) }
					className="pdf-link">{ Strings.download }</a>
			</div> }
			{ !isEmptyStringOrNull( docs.invioceUrl ) && <div className="pdf-tempalte">
				<div>
                    <span className="pdf-icon">
                        <i className="icon-pdf-o file-formate-icon"/>
                    </span>
					<span className="pdf-title">{ Strings.tax_invoice }</span>
				</div>
				<a
					onClick={ () => onOpenDoc( Strings.tax_invoice, docs.invioceUrl ) }
					className="pdf-link">{ Strings.download }</a>
			</div> }
			<div className="pdf-tempalte">
				<div>
                    <span className="pdf-icon">
                        <i className="icon-pdf-o file-formate-icon"/>
                    </span>
					<span className="pdf-title">{ Strings.statement_of_account }</span>
				</div>
				<a onClick={ () => onStatement() } className="pdf-link">{ Strings.download }</a>
			</div>
		</div>
	</div>;
}

export default MollakServiceChargeDetails;
